import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Archive, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { LoadingState, PermissionDenied } from "@/components/states";

export default function TimeActivity() {
  const [acts, setActs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        apiFetch(`/time/activity-types?include_archived=${showArchived}`),
        apiFetch("/time/settings"),
      ]);
      setActs(a); setSettings(s.settings); setCanManage(s.can_manage);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const addAct = async () => {
    if (!newName.trim()) return;
    try { await apiFetch("/time/activity-types", { method: "POST", body: JSON.stringify({ name: newName.trim() }) });
      setNewName(""); toast.success("Activity type added"); load();
    } catch (e) { toast.error(e.message); }
  };
  const archiveAct = async (a, archived) => {
    try { await apiFetch(`/time/activity-types/${a.id}/archive`, { method: "PUT", body: JSON.stringify({ archived }) }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const saveSettings = async (patch) => {
    setSaving(true);
    try { const r = await apiFetch("/time/settings", { method: "PUT", body: JSON.stringify(patch) }); setSettings(r.settings); toast.success("Saved"); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (loading) return <LoadingState label="Loading time settings…" />;
  if (!canManage) return <PermissionDenied message="You need the Manage Time Settings permission to view this page." />;

  return (
    <div className="max-w-2xl space-y-6" data-testid="time-activity-settings">
      <PageHeader eyebrow="Settings" title="Time & Activity"
        subtitle="Manage reusable activity types and organisation-wide time-tracking defaults." />

      <Card className="p-5">
        <h3 className="mb-3 font-heading text-lg">Time-tracking defaults</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Default tracking for new tasks</Label>
            <Select value={settings?.default_tracking || "off"} onValueChange={(v) => saveSettings({ default_tracking: v })}>
              <SelectTrigger data-testid="default-tracking"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Off</SelectItem>
                <SelectItem value="optional">Optional</SelectItem>
                <SelectItem value="required">Required</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Long-timer warning after (hours)</Label>
            <Input type="number" min="1" max="24" defaultValue={settings?.long_timer_hours || 4}
              onBlur={(e) => saveSettings({ long_timer_hours: Number(e.target.value) })} data-testid="long-timer-hours" />
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Time tracking is always selective — timers are never forced on tasks set to Off.</p>
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-heading text-lg">Activity types</h3>
          <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived-acts">
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
        </div>
        <div className="mb-4 flex gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New activity type…" onKeyDown={(e) => e.key === "Enter" && addAct()} data-testid="new-activity-name" />
          <Button onClick={addAct} className="gap-1.5" data-testid="add-activity"><Plus className="h-4 w-4" /> Add</Button>
        </div>
        <div className="divide-y divide-border">
          {acts.map((a) => (
            <div key={a.id} className="flex items-center gap-2 py-2.5" data-testid={`activity-${a.id}`}>
              <span className="flex-1 text-sm">{a.name}{a.is_system && <span className="ml-2 text-[10px] text-muted-foreground">default</span>}{a.archived && <span className="ml-2 text-[10px] text-warning">archived</span>}</span>
              {a.archived ? (
                <button onClick={() => archiveAct(a, false)} className="rounded p-1.5 text-muted-foreground hover:text-foreground" title="Restore"><RotateCcw className="h-4 w-4" /></button>
              ) : (
                <button onClick={() => archiveAct(a, true)} className="rounded p-1.5 text-muted-foreground hover:text-destructive" title="Archive"><Archive className="h-4 w-4" /></button>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
